import { useCallback } from 'react';
import { generatePath, useNavigate } from 'react-router-dom';

import {
	getResourceRoute,
	type ResourceAction,
	type ResourceDeclaration
} from '@/resources/resource-map';

const resolveRoutePath = (
	resource: string,
	action: ResourceAction
): ResourceDeclaration | undefined => {
	return getResourceRoute(resource, action);
};

const navigateToResource = (
	navigate: ReturnType<typeof useNavigate>,
	resource: string,
	action: ResourceAction,
	id?: string | number
) => {
	const declaration = resolveRoutePath(resource, action);

	if (!declaration) {
		console.warn(`No route declaration found for ${resource}.${action}`);
		const fallbackPath = buildFallbackPath(resource, action, id);
		if (!fallbackPath) {
			return;
		}

		navigate(fallbackPath);
		return;
	}

	const targetPath =
		id != null
			? generatePath(declaration.path, { id: String(id) })
			: declaration.path;

	navigate(targetPath);
};

const buildFallbackPath = (
	resource: string,
	action: ResourceAction,
	id?: string | number
): string | undefined => {
	const normalizedResource = resource.replace(/^\/+/, '');

	switch (action) {
		case 'list':
			return `/${normalizedResource}`;
		case 'create':
			return `/${normalizedResource}/create`;
		case 'edit':
		case 'show':
			if (id == null) {
				return undefined;
			}
			return `/${normalizedResource}/${action}/${id}`;
		default:
			return undefined;
	}
};

export const useResourceNavigation = (resource: string | undefined) => {
	const navigate = useNavigate();

	const goToEdit = useCallback(
		(id: string | number) => {
			if (!resource) {
				console.warn(
					'useResourceNavigation.goToEdit called without resource'
				);
				return;
			}

			navigateToResource(navigate, resource, 'edit', id);
		},
		[navigate, resource]
	);

	const goToCreate = useCallback(() => {
		if (!resource) {
			console.warn(
				'useResourceNavigation.goToCreate called without resource'
			);
			return;
		}

		navigateToResource(navigate, resource, 'create');
	}, [navigate, resource]);

	const goToList = useCallback(() => {
		if (!resource) {
			console.warn(
				'useResourceNavigation.goToList called without resource'
			);
			return;
		}

		navigateToResource(navigate, resource, 'list');
	}, [navigate, resource]);

	return {
		goToEdit,
		goToCreate,
		goToList
	};
};

export const useGo = () => {
	const navigate = useNavigate();

	return useCallback(
		({ to, type, state }: { to: string; type?: 'push' | 'replace'; state?: any }) => {
			if (type === 'replace') {
				navigate(to, { replace: true, state });
			} else {
				navigate(to, { state });
			}
		},
		[navigate]
	);
};
